/*
 * NAVBAR.TSX - TOP NAVIGATION COMPONENT
 * 
 * Main navigation bar component that provides access to core application features.
 * 
 * Key Features:
 * - Logo and sidebar toggle functionality
 * - Quick search for contacts
 * - Add Note button and modal management
 * - Add Task button backed by the shared task composer
 * - Chat toggle button
 * - User profile dropdown with team information and logout
 * - Theme toggle (dark/light mode)
 * 
 * Event Bus Integration:
 * - Subscribes to OPEN_NOTE_MODAL events to open New Note modal
 * - Subscribes to OPEN_TASK_MODAL events and forwards optional entity and chat draft context
 * - Integrates with AI chat service user actions
 * 
 * Modal Management:
 * - Controls NewNote modal state via local state and event bus
 * - Handles profile dropdown with click-outside detection
 * - Responsive design with mobile-specific layouts
 * - Accessible mobile navigation and compact create menu
 */

import { useTheme } from '../context/ThemeContext';
import { THEME_CLASSES } from '../constants/themeConstants';
import { EVENTS, type OpenNoteModalEventData, type OpenTaskModalEventData } from '../constants/eventConstants';
import { useState, useEffect, useRef, MouseEvent as ReactMouseEvent } from 'react';
import QuickSearch from './contact/QuickSearch';
import { getUserData, logout, ensureUserProfile } from '../services/authService';
import NewNote from './note/NewNote';
import TaskComposerDialog from './task/TaskComposerDialog';
import { Contact } from '../models/Contact';
import { useTeams } from '../context/TeamContext';
import { useEventBus } from '../context/EventBusContext';
import { Avatar, Button, Menu, MenuItem } from './primitives';

interface NavbarProps {
  onToggleModal: () => void;
  onToggleChat: () => void;
  currentContact?: Contact;
  showChatButton?: boolean;
  isNavigationOpen?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onToggleModal, onToggleChat, currentContact, showChatButton = true, isNavigationOpen = false }) => {
  const { isDark, toggleTheme } = useTheme();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isTaskComposerOpen, setIsTaskComposerOpen] = useState(false);
  const [taskComposerEntityData, setTaskComposerEntityData] = useState<OpenTaskModalEventData>({});
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState<boolean>(false);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [userData, setUserData] = useState(() => getUserData());
  const [modalEntityData, setModalEntityData] = useState<OpenNoteModalEventData>({});
  const profileDropdownRef = useRef<HTMLDivElement | null>(null);
  const createMenuRef = useRef<HTMLDivElement | null>(null);
  const { teams, currentUserId } = useTeams();
  const { subscribe, unsubscribe } = useEventBus();
  const searchPlaceholder = currentContact
    ? `Search contacts like ${[currentContact.firstName, currentContact.lastName].filter(Boolean).join(' ') || 'this'}`
    : 'Accounts / contacts';

  const toggleModal = (): void => {
    setModalOpen(!isModalOpen);
    // Reset entity data when manually opening modal
    if (!isModalOpen) {
      setModalEntityData({});
    }
  };

  // Subscribe to OPEN_NOTE_MODAL event
  useEffect(() => {
    const handleOpenNoteModal = (data: OpenNoteModalEventData = {}) => {
      setModalEntityData(data);
      setModalOpen(true);
    };

    subscribe(EVENTS.OPEN_NOTE_MODAL, handleOpenNoteModal);

    return () => {
      unsubscribe(EVENTS.OPEN_NOTE_MODAL, handleOpenNoteModal);
    };
  }, [subscribe, unsubscribe]);

  // Keep feature-level task creation on the current route.
  useEffect(() => {
    const handleOpenTaskModal = (data: OpenTaskModalEventData = {}) => {
      setTaskComposerEntityData(data);
      setIsTaskComposerOpen(true);
    };

    subscribe(EVENTS.OPEN_TASK_MODAL, handleOpenTaskModal);

    return () => {
      unsubscribe(EVENTS.OPEN_TASK_MODAL, handleOpenTaskModal);
    };
  }, [subscribe, unsubscribe]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      if (!target.closest('.profile-container')) {
        setIsProfileDropdownOpen(false);
      }
      if (!target.closest('.create-menu-container')) {
        setIsCreateMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleAuthRefresh = () => {
      setUserData(getUserData());
    };

    window.addEventListener('AUTH_REFRESH', handleAuthRefresh as EventListener);

    return () => {
      window.removeEventListener('AUTH_REFRESH', handleAuthRefresh as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!userData || userData.isAuthorized === false || userData.picture) {
      return;
    }

    void ensureUserProfile()
      .then(() => {
        setUserData(getUserData());
      })
      .catch(() => {
        // Ignore profile backfill errors and keep the initials fallback.
      });
  }, [userData]);

  const handleLogout = async (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Call logout and wait for it to complete
    // The logout function internally handles the redirect
    await logout();
  };

  return (
    <>
      <nav className="relative w-full z-50">
        <div className={`${THEME_CLASSES.background} relative`}>
          <div className={`flex items-center justify-between px-4 py-2 md:px-6 ${THEME_CLASSES.background}`}>
            {/* Left side: Logo, Search and global create actions */}
            <div className="flex items-center flex-1">
              {/* Logo */}
              <button
                type="button"
                onClick={onToggleModal}
                className="mr-4 flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                aria-label={isNavigationOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isNavigationOpen}
              >
                <svg className="text-blue-500 w-8 h-8 ml-1.5" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
              </button>
              {/* Search bar */}
              <QuickSearch
                placeholder={searchPlaceholder}
                ariaLabel="Search accounts and contacts"
                className={`min-w-32 w-[44vw] max-w-[170px] lg:w-[300px] lg:max-w-none ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder}`}
                inputClassName="!pr-3 !text-xs sm:!text-sm"
              />

              <div className="create-menu-container relative ml-2 lg:hidden" ref={createMenuRef}>
                <Button
                  id="navbar-create-menu-trigger"
                  onClick={() => setIsCreateMenuOpen((currentValue) => !currentValue)}
                  variant="icon"
                  aria-label="Create"
                  aria-haspopup="menu"
                  aria-expanded={isCreateMenuOpen}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </Button>
                {isCreateMenuOpen && (
                  <Menu labelledBy="navbar-create-menu-trigger" className="absolute left-0 top-full z-[60] mt-2 min-w-[9rem]">
                    <MenuItem
                      onClick={() => {
                        setIsCreateMenuOpen(false);
                        toggleModal();
                      }}
                    >
                      Add Note
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setIsCreateMenuOpen(false);
                        setTaskComposerEntityData({});
                        setIsTaskComposerOpen(true);
                      }}
                    >
                      Add Task
                    </MenuItem>
                  </Menu>
                )}
              </div>

              {/* Desktop-only global create actions */}
              <div className="hidden items-center gap-2 lg:ml-4 lg:flex">
                <Button
                  onClick={toggleModal}
                  size="md"
                  variant="primary"
                  className="h-9 w-28 px-3"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Note
                </Button>
                <Button
                  onClick={() => {
                    setTaskComposerEntityData({});
                    setIsTaskComposerOpen(true);
                  }}
                  size="md"
                  variant="primary"
                  className="h-9 w-28 px-3"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Task
                </Button>
              </div>
            </div>

            {/* Right side: Profile section */}
            <div className="flex items-center gap-2">
              {/* Chat button */}
              {showChatButton && (
                <button
                  onClick={() => onToggleChat()}
                  className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
                  aria-label="Open chat"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </button>
              )}

              {/* Profile container */}
              <div className="relative profile-container flex items-center" ref={profileDropdownRef}>
                <button
                  type="button"
                  id="navbar-profile-menu-trigger"
                  aria-haspopup="menu"
                  aria-expanded={isProfileDropdownOpen}
                  className="flex items-center py-2"
                  onClick={(e: ReactMouseEvent) => {
                    e.stopPropagation();
                    setIsProfileDropdownOpen(!isProfileDropdownOpen);
                  }}
                >
                  <Avatar
                    src={userData?.picture}
                    name={userData?.name}
                    size="md"
                    onError={() => {
                      void ensureUserProfile(true)
                        .then(() => {
                          setUserData(getUserData());
                        })
                        .catch(() => {
                          // Ignore profile refresh errors and keep initials fallback.
                        });
                    }}
                  />
                  <span className="hidden lg:block text-sm text-gray-700 dark:text-gray-200 ml-2">
                    {userData?.name}
                  </span>
                </button>

                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <Menu
                    labelledBy="navbar-profile-menu-trigger"
                    className="absolute right-0 top-full mt-3 z-[60]"
                  >
                    {/* Teams Section */}
                    {teams.map((team) => (
                      <MenuItem
                        key={team.teamId}
                        as="div"
                        className="cursor-default hover:bg-transparent"
                      >
                        {team.teamName}
                      </MenuItem>
                    ))}
                    {/* Divider if there are teams */}
                    {teams.length > 0 && (
                      <div className={`my-1 border-t ${THEME_CLASSES.border}`}></div>
                    )}
                    <MenuItem
                      onClick={handleLogout}
                      className="justify-end text-right"
                    >
                      Log Out
                    </MenuItem>
                  </Menu>
                )}
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className={`hidden lg:flex p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* NewNote component */}
        <NewNote
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          preSelectedContactId={modalEntityData.contactId}
          preSelectedAccountId={modalEntityData.accountId}
          preSelectedTeamId={modalEntityData.teamId}
          initialContent={modalEntityData.initialContent}
        />
      </nav>
      <TaskComposerDialog
        open={isTaskComposerOpen}
        currentUserId={currentUserId}
        task={null}
        initialEntity={taskComposerEntityData.contactId && taskComposerEntityData.contactName
          ? {
            id: taskComposerEntityData.contactId,
            name: taskComposerEntityData.contactName,
            type: 'Contact',
            accountId: taskComposerEntityData.accountId,
            accountName: taskComposerEntityData.accountName,
          }
          : taskComposerEntityData.accountId && taskComposerEntityData.accountName
            ? {
              id: taskComposerEntityData.accountId,
              name: taskComposerEntityData.accountName,
              type: 'Account',
            }
            : null}
        initialDraft={taskComposerEntityData.initialTitle
          ? {
            title: taskComposerEntityData.initialTitle,
            description: taskComposerEntityData.initialDescription,
            dueDate: taskComposerEntityData.initialDueDate,
            priority: taskComposerEntityData.initialPriority,
          }
          : null}
        onTaskSaved={() => undefined}
        onClose={() => {
          setIsTaskComposerOpen(false);
          setTaskComposerEntityData({});
        }}
      />
    </>
  );
};

export default Navbar;
